// Copyright 2021-2026 The Connect Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
var __asyncValues = (this && this.__asyncValues) || function (o) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var m = o[Symbol.asyncIterator], i;
    return m ? m.call(o) : (o = typeof __values === "function" ? __values(o) : o[Symbol.iterator](), i = {}, verb("next"), verb("throw"), verb("return"), i[Symbol.asyncIterator] = function () { return this; }, i);
    function verb(n) { i[n] = o[n] && function (v) { return new Promise(function (resolve, reject) { v = o[n](v), settle(resolve, reject, v.done, v.value); }); }; }
    function settle(resolve, reject, d, v) { Promise.resolve(v).then(function(v) { resolve({ value: v, done: d }); }, reject); }
};
var __await = (this && this.__await) || function (v) { return this instanceof __await ? (this.v = v, this) : new __await(v); }
var __asyncGenerator = (this && this.__asyncGenerator) || function (thisArg, _arguments, generator) {
    if (!Symbol.asyncIterator) throw new TypeError("Symbol.asyncIterator is not defined.");
    var g = generator.apply(thisArg, _arguments || []), i, q = [];
    return i = Object.create((typeof AsyncIterator === "function" ? AsyncIterator : Object).prototype), verb("next"), verb("throw"), verb("return", awaitReturn), i[Symbol.asyncIterator] = function () { return this; }, i;
    function awaitReturn(f) { return function (v) { return Promise.resolve(v).then(f, reject); }; }
    function verb(n, f) { if (g[n]) { i[n] = function (v) { return new Promise(function (a, b) { q.push([n, v, a, b]) > 1 || resume(n, v); }); }; if (f) i[n] = f(i[n]); } }
    function resume(n, v) { try { step(g[n](v)); } catch (e) { settle(q[0][3], e); } }
    function step(r) { r.value instanceof __await ? Promise.resolve(r.value.v).then(fulfill, reject) : settle(q[0][2], r); }
    function fulfill(value) { resume("next", value); }
    function reject(value) { resume("throw", value); }
    function settle(f, v) { if (f(v), q.shift(), q.length) resume(q[0][0], q[0][1]); }
};
import { Code, ConnectError } from "@connectrpc/connect";
import { createEnvelopeReadableStream, encodeEnvelope, } from "@connectrpc/connect/protocol";
import { decodeHeadersFrame, encodeHeadersFrame } from "./headers-frame.js";
export async function runWebTransportCall(session, requestHeaders, requestMessages, writeHeaderFlag, writeDataFlag, path) {
    const stream = await session.createBidirectionalStream();
    const writer = stream.writable.getWriter();
    // Write request headers
    const headersBytes = encodeHeadersFrame(requestHeaders, { ":path": path });
    await writer.write(encodeEnvelope(writeHeaderFlag, headersBytes));
    // Write request messages asynchronously so we can read the response concurrently
    const writePromise = (async () => {
        var _a, e_1, _b, _c;
        try {
            try {
                for (var _d = true, requestMessages_1 = __asyncValues(requestMessages), requestMessages_1_1; requestMessages_1_1 = await requestMessages_1.next(), _a = requestMessages_1_1.done, !_a; _d = true) {
                    _c = requestMessages_1_1.value;
                    _d = false;
                    const msg = _c;
                    await writer.write(encodeEnvelope(writeDataFlag, msg));
                }
            }
            catch (e_1_1) { e_1 = { error: e_1_1 }; }
            finally {
                try {
                    if (!_d && !_a && (_b = requestMessages_1.return)) await _b.call(requestMessages_1);
                }
                finally { if (e_1) throw e_1.error; }
            }
            await writer.close();
        }
        catch (err) {
            await writer.abort(err).catch(() => { });
            throw err;
        }
        finally {
            writer.releaseLock();
        }
    })();
    writePromise.catch(() => { });
    const envReader = createEnvelopeReadableStream(stream.readable).getReader();
    const firstResult = await envReader.read();
    if (firstResult.done) {
        throw new ConnectError("protocol error: missing response headers", Code.Internal);
    }
    const firstEnv = firstResult.value;
    if (firstEnv.flags !== writeHeaderFlag) {
        throw new ConnectError(`protocol error: expected headers envelope, got 0x${firstEnv.flags.toString(16)}`, Code.Internal);
    }
    const { headers: responseHeaders } = decodeHeadersFrame(firstEnv.data);
    function readResponseBody() {
        return __asyncGenerator(this, arguments, function* readResponseBody_1() {
            try {
                for (;;) {
                    const result = yield __await(Promise.race([
                        envReader.read(),
                        writePromise.then(() => new Promise(() => { })),
                    ]));
                    if (result.done) {
                        break;
                    }
                    yield yield __await(result.value);
                }
                yield __await(writePromise);
            }
            finally {
                envReader.releaseLock();
            }
        });
    }
    return {
        responseHeaders,
        responseMessages: readResponseBody(),
    };
}
